import { createMutation, createQuery, createSuspenseQuery, makeQueryKey } from "@steto-care/steto-query";
import type { SortingState } from "@tanstack/react-table";

import { UserRepository } from "@/io/repository/UserRepository.ts";
import { BGMPreferences, PatientListColumnId } from "@/models/UISettingsModel.ts";
import { isPractitionerUser } from "@/models/UserModel.ts";
import type { PatientListMenuItems } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";

export class UserQueries {
  constructor(private readonly repository: UserRepository = new UserRepository()) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useMe = createSuspenseQuery(
    "me",
    ({}) => [],
    async ({}, context) => {
      const result = await this.repository.getUser();
      if (isPractitionerUser(result)) {
        this.usePractitionerMe.manualUpdate(context.client, {}, () => result);
      }
      return result;
    },
  );

  usePractitionerMe = createSuspenseQuery(
    "me",
    ({}) => [],
    async () => {
      const user = await this.repository.getUser();
      if (!isPractitionerUser(user)) {
        throw new Error("User is not a practitioner");
      }
      return user;
    },
  );

  useFeatureFlags = createQuery(
    "featureFlags",
    ({}) => [],
    async () => this.repository.getFeatureFlags(),
  );

  useBGMPreferences = createQuery(
    "use-bgm-preferences",
    ({ userId }) => makeQueryKey(userId),
    async ({ userId }: { userId: string }) => this.repository.getBGMPreferences(userId),
  );

  usePatientListColumnVisibility = createSuspenseQuery(
    "use-patient-list-column-visibility",
    ({ userId }) => makeQueryKey(userId),
    async ({ userId }: { userId: string }) => this.repository.getPatientListColumnVisibility(userId),
  );

  usePatientListSort = createSuspenseQuery(
    "use-patient-list-sort",
    ({}) => [],
    async ({}) => {
      const me = await this.repository.getUser();
      return await this.repository.getPatientListSorting(me.id);
    },
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  useUpdateBGMPreferences = createMutation(
    "update-bgm-preferences",
    async ({ userId, data }: { userId: string; data: BGMPreferences }) =>
      this.repository.updateBGMPreferences(userId, data),
    {
      onSettled: (_, __, variables, { client }) => {
        // Invalidate the BGM preferences query
        this.useBGMPreferences.invalidate(client, variables);
      },
    },
  );

  useUpdatePatientListColumnVisibility = createMutation(
    "update-patient-list-column-visibility",
    async ({
      userId,
      tab,
      key,
      state,
    }: {
      userId: string;
      key: PatientListColumnId;
      state: boolean;
      tab: PatientListMenuItems;
    }) => this.repository.updatePatientListColumnVisibility(userId, tab, key, state),
    {
      onSettled: (_, __, variables, { client }) => {
        this.usePatientListColumnVisibility.invalidate(client, variables);
      },
    },
  );

  useResetTabToDefaults = createMutation(
    "reset-tab-to-defaults",
    async ({ userId, tab }: { userId: string; tab: PatientListMenuItems }) =>
      this.repository.resetTabToDefaults(userId, tab),
    {
      onSettled: (_, __, variables, { client }) => {
        this.usePatientListColumnVisibility.invalidate(client, variables);
      },
    },
  );

  useUpdatePatientListSort = createMutation(
    "update-patient-list-sort",
    async ({ sorting }: { sorting: SortingState }) => {
      const me = await this.repository.getUser();
      return this.repository.updatePatientListSorting(me.id, sorting);
    },
    {
      onSettled: (_, __, variables, { client }) => {
        this.usePatientListSort.invalidate(client, variables);
      },
    },
  );
}
